import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Title from "../../extra/Title";
import { apiInstanceFetch } from "../../../util/api";

const INK = "#1A1614";
const MUTED = "#8A857F";
const HAIRLINE = "#E8E1D3";
const GOLD = "#D4A129";
const CREAM = "#FFFDF7";
const REDBG = "#FDECEC";
const RED = "#DC2626";

const CommunityDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const community = location.state && location.state.community;

  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState("");
  const [openComments, setOpenComments] = useState(null);
  const [comments, setComments] = useState([]);

  const load = () =>
    apiInstanceFetch
      .get(`community/admin/posts?communityId=${id}&start=0&limit=100`)
      .then((r) => {
        setPosts(r.posts || []);
        setTotal(r.total || 0);
      })
      .catch(() => {});

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const author = (p) => {
    const u = p.userId;
    if (!u) return "Unknown";
    return ((u.firstName || "") + " " + (u.lastName || "")).trim() || u.email || "Unknown";
  };

  const removePost = (p) => {
    if (!window.confirm("Remove this post? It will be hidden from the community.")) return;
    setBusy(p._id);
    apiInstanceFetch
      .delete(`community/admin/post?postId=${p._id}`)
      .then(() => setPosts((prev) => prev.map((x) => (x._id === p._id ? { ...x, isDeleted: true } : x))))
      .catch(() => {})
      .finally(() => setBusy(""));
  };

  const toggleComments = (p) => {
    if (openComments === p._id) {
      setOpenComments(null);
      setComments([]);
      return;
    }
    setOpenComments(p._id);
    setComments([]);
    apiInstanceFetch
      .get(`community/admin/comments?postId=${p._id}`)
      .then((r) => setComments(r.comments || []))
      .catch(() => {});
  };

  return (
    <div className="mainCategory">
      <Title name="Community Detail" />

      <button
        onClick={() => navigate(-1)}
        className="btn btn-sm"
        style={{ border: `1px solid ${HAIRLINE}`, color: INK, fontWeight: 600, margin: "12px 0" }}
      >
        <i className="fa-solid fa-arrow-left" style={{ marginRight: 6 }} />
        Back
      </button>

      {/* header */}
      <div
        style={{
          background: "#fff",
          border: `1px solid ${HAIRLINE}`,
          borderRadius: 14,
          padding: 16,
          marginBottom: 16,
          display: "flex",
          gap: 14,
          alignItems: "center",
        }}
      >
        {community && community.image ? (
          <img src={community.image} alt="" width={56} height={56} style={{ borderRadius: 12, objectFit: "cover" }} />
        ) : (
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 12,
              background: CREAM,
              border: `1px solid ${HAIRLINE}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: GOLD,
              fontSize: 22,
            }}
          >
            <i className="fa-solid fa-users" />
          </div>
        )}
        <div>
          <div style={{ fontWeight: 800, color: INK, fontSize: 18 }}>{community ? community.name : "Community"}</div>
          <div style={{ fontSize: 13, color: MUTED }}>{community ? community.description : ""}</div>
          {community && (
            <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>
              {community.membersCount} members · {total} posts
            </div>
          )}
        </div>
      </div>

      {/* posts */}
      {posts.length === 0 ? (
        <div
          style={{
            background: "#fff",
            border: `1px solid ${HAIRLINE}`,
            borderRadius: 14,
            padding: 40,
            textAlign: "center",
            color: MUTED,
          }}
        >
          No posts yet
        </div>
      ) : (
        posts.map((p) => (
          <div
            key={p._id}
            style={{
              background: "#fff",
              border: `1px solid ${HAIRLINE}`,
              borderRadius: 14,
              padding: 14,
              marginBottom: 12,
              opacity: p.isDeleted ? 0.55 : 1,
            }}
          >
            <div className="d-flex align-items-center" style={{ gap: 8, marginBottom: 8 }}>
              <span style={{ fontWeight: 700, color: INK }}>{author(p)}</span>
              <span style={{ fontSize: 11.5, color: MUTED }}>{dayjs(p.createdAt).format("DD MMM YYYY HH:mm")}</span>
              {p.isDeleted && (
                <span style={{ fontSize: 11, fontWeight: 700, color: RED, background: REDBG, borderRadius: 20, padding: "1px 8px" }}>
                  Removed
                </span>
              )}
              <span style={{ marginLeft: "auto", fontSize: 12, color: MUTED }}>
                ♥ {p.likesCount} · 💬 {p.commentsCount}
              </span>
            </div>

            {p.text && <div style={{ fontSize: 14, color: INK, marginBottom: 8, whiteSpace: "pre-wrap" }}>{p.text}</div>}

            {p.media && p.media.length > 0 && (
              <div className="d-flex" style={{ gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                {p.media.map((md, i) =>
                  md.type === "video" ? (
                    <a key={i} href={md.url} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: GOLD, fontWeight: 600 }}>
                      <i className="fa-solid fa-play" style={{ marginRight: 5 }} />
                      View video
                    </a>
                  ) : (
                    <img
                      key={i}
                      src={md.url}
                      alt=""
                      style={{ width: 90, height: 90, objectFit: "cover", borderRadius: 8, border: `1px solid ${HAIRLINE}` }}
                    />
                  )
                )}
              </div>
            )}

            <div className="d-flex" style={{ gap: 8 }}>
              <button onClick={() => toggleComments(p)} className="btn btn-sm" style={{ border: `1px solid ${HAIRLINE}`, color: INK, fontWeight: 600 }}>
                <i className="fa-solid fa-comment" style={{ marginRight: 5 }} />
                {openComments === p._id ? "Hide" : "View"} comments
              </button>
              {!p.isDeleted && (
                <button
                  disabled={busy === p._id}
                  onClick={() => removePost(p)}
                  className="btn btn-sm"
                  style={{ border: "none", background: REDBG, color: RED, fontWeight: 700 }}
                >
                  <i className="fa-solid fa-trash" style={{ marginRight: 5 }} />
                  Remove
                </button>
              )}
            </div>

            {openComments === p._id && (
              <div style={{ marginTop: 10, borderTop: `1px solid ${HAIRLINE}`, paddingTop: 10 }}>
                {comments.length === 0 ? (
                  <div style={{ fontSize: 12.5, color: MUTED }}>No comments</div>
                ) : (
                  comments.map((cm) => (
                    <div key={cm._id} style={{ fontSize: 13, marginBottom: 6, color: cm.isDeleted ? MUTED : INK }}>
                      <b>
                        {(cm.userId && ((cm.userId.firstName || "") + " " + (cm.userId.lastName || "")).trim()) || "Unknown"}:
                      </b>{" "}
                      {cm.text} {cm.isDeleted && <span style={{ color: RED, fontSize: 11 }}>(deleted)</span>}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default CommunityDetail;
